import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  uploadDrakePdf,
  DuplicateChecklistError,
  PasswordProtectedPdfException,
  retryDrakeWithPassword,
  uploadDrakeBatch,
  getDrakeBatchStatus,
  uploadDrakeAsync,
  getDrakeJobStatus,
  type DrakeUploadBatch,
  type BatchUploadResponse,
  type DrakeExtractionJob,
} from '../../services/api';
import type {
  DrakeChecklist,
  PasswordProtectedPdfError,
} from '../../types';

const ACTIVE_JOB_KEY = 'drake-active-job';
const ACTIVE_BATCH_KEY = 'drake-active-batch';
const MAX_POLL_COUNT = 120; // 120 polls * 5s = 10 minutes
const STALE_JOB_MS = 30 * 60 * 1000; // 30 minutes

interface StoredJobRef {
  id: string;
  timestamp: number;
}

function getStoredJobRef(key: string): StoredJobRef | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Support legacy format (plain string ID)
    if (typeof parsed === 'string') {
      return { id: parsed, timestamp: Date.now() };
    }
    return parsed as StoredJobRef;
  } catch {
    // Legacy plain string ID
    return { id: raw, timestamp: Date.now() };
  }
}

function setStoredJobRef(key: string, id: string): void {
  const ref: StoredJobRef = { id, timestamp: Date.now() };
  localStorage.setItem(key, JSON.stringify(ref));
}

export function useDrakeUpload(taxYear: number) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<DrakeChecklist | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [existingChecklistForDuplicate, setExistingChecklistForDuplicate] = useState<DrakeChecklist | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchUploading, setBatchUploading] = useState(false);
  const [activeBatch, setActiveBatch] = useState<DrakeUploadBatch | null>(null);
  const [batchResponse, setBatchResponse] = useState<BatchUploadResponse | null>(null);
  const [activeJob, setActiveJob] = useState<DrakeExtractionJob | null>(null);
  const [jobPolling, setJobPolling] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordPdfInfo, setPasswordPdfInfo] = useState<PasswordProtectedPdfError | null>(null);
  const [pdfPassword, setPdfPassword] = useState('');
  const [passwordRetrying, setPasswordRetrying] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const batchFileInputRef = useRef<HTMLInputElement>(null);

  const invalidateChecklists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['drakeChecklists'] });
    queryClient.invalidateQueries({ queryKey: ['drakePendingReview'] });
  }, [queryClient]);

  const clearJob = useCallback(() => {
    localStorage.removeItem(ACTIVE_JOB_KEY);
    setActiveJob(null);
    setJobPolling(false);
  }, []);

  // Load active job from localStorage on mount and start polling if exists
  useEffect(() => {
    const storedRef = getStoredJobRef(ACTIVE_JOB_KEY);
    if (!storedRef) return;

    // Auto-clear stale jobs older than 30 minutes
    if (Date.now() - storedRef.timestamp > STALE_JOB_MS) {
      localStorage.removeItem(ACTIVE_JOB_KEY);
      return;
    }

    let cancelled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    setJobPolling(true);
    let pollCount = 0;
    const pollJobStatus = async () => {
      if (cancelled) return;
      pollCount++;
      if (pollCount > MAX_POLL_COUNT) {
        if (!cancelled) {
          setUploadError('Processing timed out. The job may still be running — check back later or try re-uploading.');
          localStorage.removeItem(ACTIVE_JOB_KEY);
          setJobPolling(false);
        }
        return;
      }
      try {
        const job = await getDrakeJobStatus(storedRef.id);
        if (cancelled) return;
        setActiveJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          localStorage.removeItem(ACTIVE_JOB_KEY);
          setJobPolling(false);
          invalidateChecklists();
        } else {
          timeoutHandle = setTimeout(pollJobStatus, 5000);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error polling job status:', err);
        setUploadError(`Failed to check job status: ${err instanceof Error ? err.message : 'Unknown error'}`);
        localStorage.removeItem(ACTIVE_JOB_KEY);
        setActiveJob(null);
        setJobPolling(false);
      }
    };
    pollJobStatus();
    return () => {
      cancelled = true;
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, [invalidateChecklists]);

  // Load active batch from localStorage on mount and resume polling if exists
  useEffect(() => {
    const storedRef = getStoredJobRef(ACTIVE_BATCH_KEY);
    if (!storedRef) return;

    // Auto-clear stale batches older than 30 minutes
    if (Date.now() - storedRef.timestamp > STALE_JOB_MS) {
      localStorage.removeItem(ACTIVE_BATCH_KEY);
      return;
    }

    let cancelled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    setBatchUploading(true);
    let pollCount = 0;
    const pollBatchStatus = async () => {
      if (cancelled) return;
      pollCount++;
      if (pollCount > MAX_POLL_COUNT) {
        if (!cancelled) {
          setUploadError('Batch processing timed out. The batch may still be running — check back later or try re-uploading.');
          localStorage.removeItem(ACTIVE_BATCH_KEY);
          setBatchUploading(false);
        }
        return;
      }
      try {
        const batch = await getDrakeBatchStatus(storedRef.id);
        if (cancelled) return;
        setActiveBatch(batch);
        if (batch.status === 'completed' || batch.status === 'failed') {
          localStorage.removeItem(ACTIVE_BATCH_KEY);
          setBatchUploading(false);
          invalidateChecklists();
        } else {
          timeoutHandle = setTimeout(pollBatchStatus, 5000);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error polling batch status:', err);
        setUploadError(`Failed to check batch status: ${err instanceof Error ? err.message : 'Unknown error'}`);
        localStorage.removeItem(ACTIVE_BATCH_KEY);
        setActiveBatch(null);
        setBatchUploading(false);
      }
    };
    pollBatchStatus();
    return () => {
      cancelled = true;
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, [invalidateChecklists]);

  const handleFileUpload = useCallback(async (files: FileList | null, overwrite: boolean = false) => {
    if (!files || files.length === 0) return;

    if (overwrite) {
      setUploading(true);
      setUploadError(null);
      setUploadResult(null);
      try {
        const file = files[0];
        const result = await uploadDrakePdf(file, taxYear, undefined, undefined, true);
        setUploadResult(result);
        invalidateChecklists();
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    setActiveJob(null);

    try {
      const file = files[0];
      const job = await uploadDrakeAsync(file, taxYear);
      setStoredJobRef(ACTIVE_JOB_KEY, job.id);
      setActiveJob(job);
      setJobPolling(true);
      setUploading(false);

      let pollCount = 0;
      const pollJobStatus = async () => {
        pollCount++;
        if (pollCount > MAX_POLL_COUNT) {
          setUploadError('Processing timed out. The job may still be running — check back later or try re-uploading.');
          localStorage.removeItem(ACTIVE_JOB_KEY);
          setJobPolling(false);
          return;
        }
        try {
          const updatedJob = await getDrakeJobStatus(job.id);
          setActiveJob(updatedJob);
          if (updatedJob.status === 'completed' || updatedJob.status === 'failed') {
            localStorage.removeItem(ACTIVE_JOB_KEY);
            setJobPolling(false);
            invalidateChecklists();
          } else {
            setTimeout(pollJobStatus, 5000);
          }
        } catch (err) {
          console.error('Error polling job status:', err);
          setUploadError(`Failed to check job status: ${err instanceof Error ? err.message : 'Unknown error'}`);
          localStorage.removeItem(ACTIVE_JOB_KEY);
          setActiveJob(null);
          setJobPolling(false);
        }
      };
      setTimeout(pollJobStatus, 2000);
    } catch (err) {
      if (err instanceof DuplicateChecklistError) {
        setExistingChecklistForDuplicate(err.existingChecklist);
        setPendingFile(files[0]);
        setDuplicateDialogOpen(true);
        setUploading(false);
        return;
      }
      if (err instanceof PasswordProtectedPdfException) {
        setPasswordPdfInfo(err.pdfError);
        setPdfPassword('');
        setPasswordError(null);
        setPasswordDialogOpen(true);
        setUploading(false);
        return;
      }
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setUploading(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [taxYear, invalidateChecklists]);

  const handlePasswordRetry = useCallback(async () => {
    if (!passwordPdfInfo || !pdfPassword) return;
    setPasswordRetrying(true);
    setPasswordError(null);
    try {
      const result = await retryDrakeWithPassword({
        blobName: passwordPdfInfo.blobName,
        password: pdfPassword,
        taxYear: passwordPdfInfo.taxYear,
      });
      setUploadResult(result);
      setPasswordDialogOpen(false);
      setPasswordPdfInfo(null);
      setPdfPassword('');
      invalidateChecklists();
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to extract PDF with password');
    } finally {
      setPasswordRetrying(false);
    }
  }, [passwordPdfInfo, pdfPassword, invalidateChecklists]);

  const handleBatchUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBatchUploading(true);
    setBatchResponse(null);
    setActiveBatch(null);
    setUploadError(null);
    try {
      const fileArray = Array.from(files);
      const response = await uploadDrakeBatch(fileArray, taxYear);
      setBatchResponse(response);
      setStoredJobRef(ACTIVE_BATCH_KEY, response.batchId);
      let pollCount = 0;
      const pollBatchStatus = async () => {
        pollCount++;
        if (pollCount > MAX_POLL_COUNT) {
          setUploadError('Batch processing timed out. The batch may still be running — check back later or try re-uploading.');
          localStorage.removeItem(ACTIVE_BATCH_KEY);
          setBatchUploading(false);
          return;
        }
        try {
          const batch = await getDrakeBatchStatus(response.batchId);
          setActiveBatch(batch);
          if (batch.status === 'completed' || batch.status === 'failed') {
            localStorage.removeItem(ACTIVE_BATCH_KEY);
            invalidateChecklists();
            setBatchUploading(false);
          } else {
            setTimeout(pollBatchStatus, 5000);
          }
        } catch (err) {
          console.error('Error polling batch status:', err);
          setUploadError(`Failed to check batch status: ${err instanceof Error ? err.message : 'Unknown error'}`);
          localStorage.removeItem(ACTIVE_BATCH_KEY);
          setBatchUploading(false);
        }
      };
      setTimeout(pollBatchStatus, 2000);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Batch upload failed');
      setBatchUploading(false);
    } finally {
      if (batchFileInputRef.current) batchFileInputRef.current.value = '';
    }
  }, [taxYear, invalidateChecklists]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const clearBatch = useCallback(() => {
    localStorage.removeItem(ACTIVE_BATCH_KEY);
    setActiveBatch(null);
    setBatchResponse(null);
    setBatchUploading(false);
  }, []);

  return {
    // Upload state
    uploading,
    uploadResult,
    setUploadResult,
    uploadError,
    // Batch state
    isBatchMode,
    setIsBatchMode,
    batchUploading,
    activeBatch,
    setActiveBatch,
    batchResponse,
    setBatchResponse,
    // Job state
    activeJob,
    setActiveJob,
    jobPolling,
    clearJob,
    // Password state
    passwordDialogOpen,
    setPasswordDialogOpen,
    passwordPdfInfo,
    setPasswordPdfInfo,
    pdfPassword,
    setPdfPassword,
    passwordRetrying,
    passwordError,
    setPasswordError,
    // Duplicate state
    duplicateDialogOpen,
    setDuplicateDialogOpen,
    existingChecklistForDuplicate,
    setExistingChecklistForDuplicate,
    pendingFile,
    setPendingFile,
    // Refs
    fileInputRef,
    batchFileInputRef,
    // Handlers
    handleFileUpload,
    handlePasswordRetry,
    handleBatchUpload,
    handleDrop,
    handleDragOver,
    clearBatch,
  };
}
